import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { useId, useMemo, useState } from 'react';
import { actionToolLabel, describeActionParameters } from './actionParameters.js';
import { isWideConfirm } from './paneHost.js';
/** The locale the person's dates are written in: the page's language when the runtime can format it. */
function pageLocale() {
    try {
        const language = typeof navigator === 'undefined' ? undefined : navigator.language;
        if (language) {
            new Intl.DateTimeFormat(language);
            return language;
        }
    }
    catch { /* an unusual language tag: the runtime's own locale */ }
    return undefined;
}
/**
 * The quiet line under an action's summary: "Microsoft 365 · Create event" when the connector is known, otherwise the
 * tool id in muted small text. The id is always in the tooltip, and the exact view prints it.
 */
export function ActionToolLine({ tool, app, appLabel, suffix }) {
    const label = actionToolLabel(tool, app, appLabel);
    return _jsxs("div", { className: "gyro-action-tool", "data-tool": tool, title: `Tool: ${tool}`, children: [label ?? tool, suffix ?? ''] });
}
function ReadableValueView({ value }) {
    switch (value.kind) {
        case 'text':
            return _jsxs(_Fragment, { children: [value.text, value.cut !== undefined && _jsx("span", { className: "gyro-action-value-note", children: value.cut })] });
        case 'time':
            return _jsx("span", { title: value.exact, children: value.text });
        case 'note':
            return _jsx("span", { className: "gyro-action-value-note", "data-redacted": value.redacted || undefined, children: value.text });
        case 'list':
            return _jsxs(_Fragment, { children: [_jsx("ul", { className: "gyro-action-list", children: value.items.map((item, index) => _jsx("li", { children: _jsx(ReadableValueView, { value: item }) }, index)) }), value.more !== undefined && _jsx("span", { className: "gyro-action-value-note", children: value.more })] });
        case 'fields':
            return _jsx(ReadableRows, { rows: value.rows, more: value.more, nested: true });
        case 'exact':
            return _jsx("code", { className: "gyro-action-exact", children: value.json });
    }
}
function ReadableRows({ rows, more, nested }) {
    return _jsxs(_Fragment, { children: [_jsx("dl", { className: `gyro-action-fields${nested ? ' nested' : ''}`, children: rows.map((row) => (_jsxs("div", { className: "gyro-action-field", "data-keys": row.keys.join(' '), children: [_jsx("dt", { children: row.label }), _jsx("dd", { children: _jsx(ReadableValueView, { value: row.value }) })] }, row.keys.join('\u0000')))) }), more !== undefined && _jsx("span", { className: "gyro-action-value-note", children: more })] });
}
/**
 * A confirm card's details: the readable view by default, one labelled row per field (actionParameters.ts), and "Show
 * exact values", one click away, for the exact view: the tool id and the bounded JSON the card has always shown, byte for
 * byte. When the exact view is cut at its total limit the card shows it alone. What runs is always exactly the input.
 */
export function ActionParameterDetails({ tool, input, limits, open }) {
    const [exact, setExact] = useState(false);
    const exactId = useId();
    const { maxStringChars, maxTotalChars } = limits;
    const shown = useMemo(() => describeActionParameters(input, { maxStringChars, maxTotalChars }, { locale: pageLocale() }), [input, maxStringChars, maxTotalChars]);
    const wide = isWideConfirm(limits);
    const showExact = exact || !shown.readable;
    return _jsxs("details", { className: "gyro-action-parameters", open: open, children: [_jsx("summary", { children: "Details" }), showExact
                ? _jsxs("div", { className: "gyro-action-exact-view", id: exactId, children: [_jsxs("div", { className: "gyro-action-tool", children: ["tool: ", _jsx("code", { children: tool })] }), wide
                            ? _jsx("pre", { className: "gyro-action-parameters-full", tabIndex: 0, "aria-label": "Action parameters, scrollable", children: shown.text })
                            : _jsx("pre", { children: shown.text })] })
                : _jsx("div", { className: `gyro-action-readable${wide ? ' gyro-action-readable-full' : ''}`, tabIndex: 0, "aria-label": "Action details, scrollable", children: _jsx(ReadableRows, { rows: shown.readable.rows, more: shown.readable.more }) }), shown.readable && (_jsx("button", { type: "button", className: "gyro-action-parameters-toggle", "aria-expanded": exact, "aria-controls": exact ? exactId : undefined, onClick: () => setExact((value) => !value), children: exact ? 'Show readable details' : 'Show exact values' })), !shown.complete && (_jsx("small", { children: "Parameters exceed the safe display limit. This action cannot be confirmed." }))] });
}
//# sourceMappingURL=ActionDetails.js.map